import { useEffect } from 'react';
import styled from 'styled-components';
import WebApp from '@twa-dev/sdk';
import { toast } from 'react-toastify';
import { useTranslation, Trans } from 'react-i18next';
import useReferralStore from '../stores/referralStore';
import CurrencyIcon from './CurrencyIcon';

const IMAGE_BASE_URL = process.env.REACT_APP_IMAGE_BASE_URL || '';

const Skeleton = styled.div`
	background: rgba(200,200,200,0.15);
	border-radius: 8px;
	min-height: 24px;
	min-width: 40px;
	width: ${props => props.width || '100%'};
	height: ${props => props.height || '24px'};
	margin: 0 auto;
	animation: skeleton-loading 1.2s infinite linear;
	@keyframes skeleton-loading {
		0% { opacity: 0.5; }
		50% { opacity: 1; }
		100% { opacity: 0.5; }
	}
`;

const Container = styled.div`
	min-height: 100vh;
	background: ${props => props.theme.background};
	padding: 0 16px;
	box-sizing: border-box;

	/* Скрываем скроллбар, но оставляем функциональность */
	scrollbar-width: none;
	-ms-overflow-style: none;
	&::-webkit-scrollbar {
		display: none;
	}
`;

const ReferralSection = styled.div`
	background: ${props => props.theme.card.background};
	border-radius: 16px;
	padding: 20px;
	margin-bottom: 20px;
	margin-top: 50px;
	box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
`;

const Title = styled.h2`
	margin: 0 0 16px 0;
	font-size: 20px;
	color: ${props => props.theme.text.primary};
`;

const ShareButton = styled.button`
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 8px;
	width: 100%;
	padding: 12px;
	background: ${props => props.theme.primary};
	color: white;
	border: none;
	border-radius: 12px;
	font-size: 16px;
	cursor: pointer;
	transition: opacity 0.2s;

	&:hover {
		opacity: 0.9;
	}

	svg {
		font-size: 18px;
	}
`;

const StatsGrid = styled.div`
	display: grid;
	grid-template-columns: repeat(2, 1fr);
	gap: 16px;
	margin-bottom: 20px;
`;

const StatCard = styled.div`
	background: ${props => props.theme.card.overlay};
	border-radius: 12px;
	padding: 16px;
	text-align: center;
	position: relative;
	cursor: help;

	&:hover::after {
		content: attr(data-tooltip);
		position: absolute;
		bottom: 100%;
		left: 50%;
		transform: translateX(-50%);
		background: rgba(0, 0, 0, 0.8);
		color: white;
		padding: 8px 12px;
		border-radius: 8px;
		font-size: 12px;
		white-space: nowrap;
		z-index: 1000;
		margin-bottom: 8px;
	}
`;

const StatValue = styled.div`
	font-size: 24px;
	font-weight: bold;
	color: ${props => props.theme.text.primary};
	margin-bottom: 4px;
`;

const StatLabel = styled.div`
	font-size: 14px;
	color: ${props => props.theme.text.secondary};
`;

const ReferralsList = styled.div`
	margin-top: 24px;
`;

const ReferralItem = styled.div`
	display: flex;
	align-items: center;
	gap: 12px;
	padding: 12px;
	background: ${props => props.theme.card.overlay};
	border-radius: 8px;
	margin-bottom: 8px;

	&:last-child {
		margin-bottom: 0;
	}
`;

const Avatar = styled.img`
	width: 40px;
	height: 40px;
	border-radius: 50%;
	object-fit: cover;
`;

const UserInfo = styled.div`
	flex: 1;
`;

const Username = styled.div`
	font-size: 16px;
	color: ${props => props.theme.text.primary};
`;

const JoinDate = styled.div`
	font-size: 12px;
	color: ${props => props.theme.text.secondary};
`;

const TokensEarned = styled.div`
	font-size: 14px;
	color: ${props => props.theme.primary};
	font-weight: 500;
	margin-top: 4px;
`;

const ReferralCodeSection = styled.div`
	background: ${props => props.theme.card.background};
	border-radius: 16px;
	padding: 20px;
	margin-bottom: 20px;
	box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
`;

const ReferralCodeTitle = styled.h3`
	margin: 0 0 16px 0;
	font-size: 18px;
	color: ${props => props.theme.text.primary};
`;

const BotInfo = styled.div`
	font-size: 14px;
	color: ${props => props.theme.text.secondary};
	margin-bottom: 12px;
`;

const ReferralCodeContainer = styled.div`
	display: flex;
	align-items: center;
	gap: 12px;
	background: ${props => props.theme.card.overlay};
	border-radius: 12px;
	padding: 12px;
`;

const ReferralCode = styled.div`
	flex: 1;
	font-size: 16px;
	font-weight: 500;
	color: ${props => props.theme.text.primary};
	letter-spacing: 1px;
`;

const CopyButton = styled.button`
	display: flex;
	align-items: center;
	gap: 8px;
	background: ${props => props.theme.primary};
	color: white;
	border: none;
	border-radius: 8px;
	padding: 8px 16px;
	font-size: 14px;
	cursor: pointer;
	transition: opacity 0.2s;

	&:hover {
		opacity: 0.9;
	}

	svg {
		font-size: 16px;
	}
`;

const HeaderBlock = styled.div`
	display: flex;
	flex-direction: column;
	align-items: center;
	margin-bottom: 24px;
`;

const HeaderText = styled.div`
	font-size: 16px;
	color: ${props => props.theme.text.primary};
	text-align: center;
	margin-top: 4px;
`;

const PaginationContainer = styled.div`
	display: flex;
	justify-content: center;
	align-items: center;
	gap: 12px;
	margin-top: 24px;
`;

const PageButton = styled.button`
	background: ${props => props.theme.card.background};
	color: ${props => props.theme.text.primary};
	border: 1px solid ${props => props.theme.border};
	border-radius: 8px;
	padding: 6px 14px;
	font-size: 15px;
	cursor: pointer;
	transition: background 0.2s;
	&:disabled {
		opacity: 0.5;
		cursor: not-allowed;
	}
`;

const FixedInviteButton = styled.button`
	position: fixed;
	left: 16px;
	right: 16px;
	bottom: calc(var(--tg-safe-area-inset-bottom, 0px) + 82px);
	height: 50px;
	z-index: 1000;
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 8px;
	padding: 18px 16px 18px 16px;
	border-radius: 12px;
	background: ${props => props.theme.primary};
	color: white;
	border: none;
	font-size: 18px;
	font-weight: 600;
	cursor: pointer;
	box-shadow: 0 -2px 12px rgba(0,0,0,0.08);
	transition: opacity 0.2s;

	&:hover {
		opacity: 0.93;
	}

	svg {
		font-size: 20px;
	}
`;

const DefaultAvatar = styled.div`
	width: 40px;
	height: 40px;
	border-radius: 50%;
	background: #2563eb;
	color: #fff;
	display: flex;
	align-items: center;
	justify-content: center;
	font-size: 18px;
	font-weight: 600;
`;

const getBotUsername = () => {
	return process.env.REACT_APP_BOT_USERNAME || '';
};

const Referrals = () => {
	const { t, i18n } = useTranslation();
	console.log('Current language:', i18n.language);
	console.log('Translation test:', t('common.shareReferral'));

	const {
		referralCode,
		stats,
		referrals,
		loading,
		error,
		fetchReferrals,
		pagination,
		setPage,
		lastUpdated
	} = useReferralStore();

	useEffect(() => {
		const now = Date.now();
		const shouldForceUpdate = !lastUpdated || (now - lastUpdated) >= 60000;

		if (!referrals.length || shouldForceUpdate) {
			fetchReferrals(pagination.page, shouldForceUpdate);
		}

		// Проверяем наличие реферального кода при инициализации
		// const startParam = WebApp.initDataUnsafe?.start_param;
		// if (startParam?.startsWith('ref_')) {
		// 	const refCode = startParam.substring(4);
		// 	handleReferralCode(refCode);
		// }
		// eslint-disable-next-line
	}, [pagination.page, lastUpdated]);

	// const handleReferralCode = async (refCode) => {
	// 	try {
	// 		await apiService.submitReferralCode(refCode);
	// 		toast.success(t('friends.referralCodeApplied'));
	// 	} catch (error) {
	// 		console.error('Error submitting referral code:', error);
	// 		toast.error(t('friends.referralCodeError'));
	// 	}
	// };

	const handleShareReferral = () => {
		const botUsername = getBotUsername();
		const refLink = `https://t.me/${botUsername}?startapp=ref_${referralCode || ''}`;

		if (WebApp.isVersionAtLeast('6.0')) {
			WebApp.openTelegramLink(`https://t.me/share/url?url=${encodeURIComponent(refLink)}&text=${encodeURIComponent(t('common.shareReferral'))}`);
		} else {
			navigator.clipboard.writeText(refLink);
			toast.info(t('common.linkCopied'));
		}
	};

	const handleCopyReferralCode = () => {
		const botUsername = getBotUsername();
		if (!botUsername) {
			toast.error(t('friends.botUsernameError'));
			return;
		}
		const refLink = `https://t.me/${botUsername}?startapp=ref_${referralCode || ''}`;
		navigator.clipboard.writeText(refLink);
		toast.success(t('friends.copySuccess'));
	};

	const handlePrevPage = () => {
		if (pagination.page > 1) {
			setPage(pagination.page - 1);
			fetchReferrals(pagination.page - 1, false);
		}
	};

	const handleNextPage = () => {
		if (pagination.page < pagination.totalPages) {
			setPage(pagination.page + 1);
			fetchReferrals(pagination.page + 1, false);
		}
	};

	if (error) {
		return <Container><div style={{ textAlign: 'center', color: 'red', padding: '20px' }}>{error}</div></Container>;
	}

	return (
		<Container>
			{/* <HeaderBlock>
				<FaGift size={40} style={{ color: '#3399ff', marginBottom: 8 }} />
				<HeaderText>{t('friends.headerText')}</HeaderText>
			</HeaderBlock> */}

			<ReferralSection>
				<Title>{t('friends.programTitle')}</Title>
				<StatsGrid>
					<StatCard data-tooltip={t('friends.invitedTooltip')}>
						<StatValue>
							{loading ? <Skeleton width="40px" height="28px" /> : stats.invitedCount}
						</StatValue>
						<StatLabel>{t('friends.invitedLabel')}</StatLabel>
					</StatCard>
					<StatCard data-tooltip={t('friends.activeTooltip')}>
						<StatValue>
							{loading ? <Skeleton width="40px" height="28px" /> : stats.activeUsers}
						</StatValue>
						<StatLabel>{t('friends.activeLabel')}</StatLabel>
					</StatCard>
				</StatsGrid>
			</ReferralSection>

			{/* <ReferralCodeSection>
				<ReferralCodeTitle>{t('friends.referralCodeTitle')}</ReferralCodeTitle>
				<BotInfo>
					{t('friends.bot')}: @{getBotUsername() || (loading ? <Skeleton width="80px" height="14px" style={{ display: 'inline-block', verticalAlign: 'middle' }} /> : '')}
				</BotInfo>
				<ReferralCodeContainer>
					<ReferralCode>{loading ? <Skeleton width="120px" height="16px" /> : referralCode}</ReferralCode>
					<CopyButton onClick={handleCopyReferralCode}>
						<FaCopy />
						{t('friends.copy')}
					</CopyButton>
				</ReferralCodeContainer>
			</ReferralCodeSection> */}

			<ReferralsList>
				<Title>{t('friends.referralsTitle')}</Title>
				{loading
					? Array.from({ length: 3 }).map((_, i) => (
						<ReferralItem key={i}>
							<Avatar as={Skeleton} width="40px" height="40px" />
							<UserInfo>
								<Username><Skeleton width="120px" height="18px" /></Username>
								<JoinDate><Skeleton width="80px" height="14px" /></JoinDate>
							</UserInfo>
						</ReferralItem>
					))
					: referrals.map((referral) => (
						<ReferralItem key={referral.telegramId}>
							{referral.avatarUrl ? (
								<Avatar
									src={`${IMAGE_BASE_URL}${referral.avatarUrl}`}
									alt={referral.username}
								/>
							) : (
								<DefaultAvatar>
									{`${referral.firstName?.[0] || ''}${referral.lastName?.[0] || ''}`.toUpperCase() || '?'}
								</DefaultAvatar>
							)}
							<UserInfo>
								<Username>
									{referral.firstName} {referral.lastName}
									{referral.username && ` (@${referral.username})`}
								</Username>
								<JoinDate>
									{t('friends.joined', { date: new Date(referral.joinedAt).toLocaleDateString() })}
									{referral.firstCaseOpenedAt && (
										<> • {t('friends.openedCase', { date: new Date(referral.firstCaseOpenedAt).toLocaleDateString() })}</>
									)}
								</JoinDate>
								<TokensEarned>
									{t('friends.earnedTokens', { amount: referral.tokensEarned })} <CurrencyIcon size={16} />
								</TokensEarned>
							</UserInfo>
						</ReferralItem>
					))}

				{!loading && referrals.length === 0 && (
					<div style={{ textAlign: 'center', color: '#666', padding: '20px' }}>
						{t('friends.noReferrals')}
					</div>
				)}
			</ReferralsList>

			{pagination.totalPages > 1 && (
				<PaginationContainer>
					<PageButton onClick={handlePrevPage} disabled={pagination.page === 1}>
						{t('common.prev')}
					</PageButton>
					<span>{pagination.page} / {pagination.totalPages}</span>
					<PageButton onClick={handleNextPage} disabled={pagination.page === pagination.totalPages}>
						{t('common.next')}
					</PageButton>
				</PaginationContainer>
			)}

			{/* Фиксированная кнопка приглашения */}
			<FixedInviteButton onClick={handleShareReferral}>
				<Trans
					i18nKey="case.inviteFriendReward"
					components={{ currency: <CurrencyIcon size={16} /> }}
				/>
			</FixedInviteButton>
		</Container>
	);
};

export default Referrals;